import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Flame, LayoutList, KanbanSquare, Loader2 } from 'lucide-react';
import { DISPLAY_COLUMNS, _displayColumnFor } from './Maintenance';
import { ComplianceCloseoutPrompt, closeoutStageFor } from './ComplianceCloseout';

const API = process.env.REACT_APP_BACKEND_URL;

export const STATUS_STYLE = {
  expired: { label: 'Expired', cls: 'bg-[#E2445C] text-white' },
  missing: { label: 'MISSING', cls: 'bg-[#E2445C] text-white' },
  unreadable: { label: 'Expiry unreadable', cls: 'bg-[#E2445C]/10 text-[#c83248]' },
  urgent: { label: 'Expiring', cls: 'bg-[#E2445C]/10 text-[#c83248]' },
  renewal_in_progress: { label: 'Renewal in progress', cls: 'bg-[#579BFC]/10 text-[#2475e6]' },
  expiring: { label: 'Expiring', cls: 'bg-[#FFCB00]/20 text-[#8a6d00]' },
  warning: { label: 'Check gas supply', cls: 'bg-[#FFCB00]/20 text-[#8a6d00]' },
  valid: { label: 'Valid', cls: 'bg-[#00CA72]/10 text-[#009e5a]' },
  not_required: { label: 'Not required', cls: 'bg-[#C3C6D4]/40 text-[#676879]' },
};
const SUMMARY = [
  { key: 'attention', label: 'Expired or missing', statuses: ['expired', 'missing', 'unreadable'], dot: 'bg-[#E2445C]' },
  { key: 'soon', label: 'Expiring or to check', statuses: ['urgent', 'expiring', 'warning'], dot: 'bg-[#FFCB00]' },
  { key: 'progress', label: 'Renewal in progress', statuses: ['renewal_in_progress'], dot: 'bg-[#579BFC]' },
  { key: 'ok', label: 'Valid', statuses: ['valid', 'not_required'], dot: 'bg-[#00CA72]' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function daysText(r) {
  if (r.days_remaining == null) return '—';
  if (r.days_remaining < 0) return `${Math.abs(r.days_remaining)} days overdue`;
  if (r.days_remaining === 0) return 'Today';
  return `${r.days_remaining} days`;
}

export default function ComplianceBoard({ refreshKey }) {
  const [view, setView] = useState('register');
  const [board, setBoard] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [b, m] = await Promise.all([
        fetch(`${API}/api/compliance/board`, { credentials: 'include' }),
        fetch(`${API}/api/maintenance/requests`, { credentials: 'include' }),
      ]);
      if (!b.ok) throw new Error('Could not load the compliance register');
      setBoard(await b.json());
      const cutoff = Date.now() - 14 * 86400000;
      if (m.ok) setJobs(((await m.json()) || []).filter((r) => r.is_compliance === true && !r.archived
        && !(r.compliance_closed_at && new Date(r.compliance_closed_at).getTime() < cutoff)));
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="flex flex-col gap-5" data-testid="compliance-board">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-[#E6E9EF] bg-white p-1" role="tablist">
          {[['register', 'Register', LayoutList], ['pipeline', 'Pipeline', KanbanSquare]].map(([k, l, Icon]) => (
            <button key={k} type="button" role="tab" aria-selected={view === k} data-testid={`compliance-view-${k}`}
              onClick={() => setView(k)}
              className={`inline-flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${view === k ? 'bg-[#0073EA] text-white shadow-sm' : 'text-[#676879] hover:bg-[#F4F5F7]'}`}>
              <Icon size={16} /> {l}{k === 'pipeline' && jobs.length ? ` · ${jobs.length}` : ''}
            </button>
          ))}
        </div>
        {board && <span className="text-xs font-semibold text-[#676879]">As of {fmtDate(board.today)}</span>}
      </div>
      {error && <p className="text-sm text-[#c83248]" data-testid="compliance-board-error">{error}</p>}
      {!board && !error && <Loader2 className="animate-spin text-[#0073EA]" />}
      {board && view === 'register' && (
        <>
          <SummaryStrip counts={board.counts} filter={filter} setFilter={setFilter} />
          <RegisterTable rows={board.rows} filter={filter} onChanged={load} />
        </>
      )}
      {board && view === 'pipeline' && <Pipeline jobs={jobs} onChanged={load} />}
    </div>
  );
}

function SummaryStrip({ counts, filter, setFilter }) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="compliance-summary">
      {SUMMARY.map((s, i) => {
        const n = s.statuses.reduce((a, k) => a + (counts[k] || 0), 0);
        const active = filter === s.key;
        return (
          <button key={s.key} type="button" data-testid={`compliance-summary-${s.key}`}
            onClick={() => setFilter(active ? null : s.key)}
            style={{ animationDelay: `${i * 60}ms` }}
            className={`animate-in fade-in slide-in-from-bottom-1 flex flex-col gap-1 rounded-xl border bg-white p-4 text-left transition-[transform,border-color] duration-200 hover:-translate-y-0.5 ${active ? 'border-[#0073EA] ring-2 ring-[#0073EA]/15' : 'border-[#E6E9EF]'}`}>
            <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#676879]">
              <span className={`h-2 w-2 rounded-full ${s.dot}`} /> {s.label}
            </span>
            <span className="text-3xl font-bold tracking-tight text-[#323338]" style={{ fontFamily: 'Satoshi, sans-serif' }}>{n}</span>
          </button>
        );
      })}
    </div>
  );
}

function RegisterTable({ rows, filter, onChanged }) {
  const [openHmo, setOpenHmo] = useState({});
  const statuses = filter ? SUMMARY.find((s) => s.key === filter).statuses : null;
  const { main, hmo } = useMemo(() => {
    const m = [], h = {};
    rows.forEach((r) => {
      if (r.hmo_group) (h[r.property_id] = h[r.property_id] || []).push(r);
      else m.push(r);
    });
    return { main: m, hmo: h };
  }, [rows]);
  const visible = (r) => !statuses || statuses.includes(r.status);
  const seenHmo = new Set();

  return (
    <div className="w-full overflow-x-auto rounded-xl border border-[#E6E9EF] bg-white shadow-sm" data-testid="compliance-register">
      <table className="w-full min-w-[760px]">
        <thead>
          <tr>
            {['Property', 'Certificate', 'Expiry', 'Days remaining', 'Status'].map((h) => (
              <th key={h} className="border-b border-[#E6E9EF] bg-[#F4F5F7] px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[#676879]">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {main.filter(visible).map((r) => {
            const group = hmo[r.property_id];
            const showGroup = group && !seenHmo.has(r.property_id) && group.some(visible);
            if (showGroup) seenHmo.add(r.property_id);
            return (
              <React.Fragment key={`${r.property_id}-${r.item}`}>
                <RegisterRow r={r} onChanged={onChanged} />
                {showGroup && (
                  <HmoGroup rows={group.filter(visible)} open={!!openHmo[r.property_id]}
                    toggle={() => setOpenHmo({ ...openHmo, [r.property_id]: !openHmo[r.property_id] })} />
                )}
              </React.Fragment>
            );
          })}
          {Object.entries(hmo).filter(([pid, g]) => !seenHmo.has(pid) && g.some(visible)).map(([pid, g]) => (
            <HmoGroup key={`hmo-${pid}`} rows={g.filter(visible)} open={!!openHmo[pid]} withProperty
              toggle={() => setOpenHmo({ ...openHmo, [pid]: !openHmo[pid] })} />
          ))}
          {!main.some(visible) && !Object.values(hmo).some((g) => g.some(visible)) && (
            <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-[#676879]" data-testid="compliance-register-empty">Nothing here.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function RegisterRow({ r, onChanged, nested }) {
  const st = STATUS_STYLE[r.status];
  return (
    <tr className="border-b border-[#E6E9EF] transition-colors hover:bg-[#F8F8FA]" data-testid={`register-row-${r.property_id}-${r.item}`}>
      <td className={`px-5 py-3.5 text-sm font-medium text-[#323338] ${nested ? 'pl-12' : ''}`}>
        {nested ? <span className="text-[#676879]">↳</span> : (
          <Link to={`/properties/${r.property_id}`} className="text-[#0073EA] hover:underline">{r.property}</Link>
        )}
      </td>
      <td className="px-5 py-3.5 text-sm font-semibold text-[#323338]">
        {r.label}
        {r.note && <span className="block text-xs font-medium text-[#676879]">{r.note}</span>}
        {r.served_to_tenant === false && r.status !== 'missing' && (
          <span className="mt-0.5 block text-xs font-semibold text-[#b38e00]" data-testid={`register-not-served-${r.property_id}-${r.item}`}>Not yet served on tenant</span>
        )}
      </td>
      <td className="px-5 py-3.5 text-sm text-[#323338]">{fmtDate(r.expiry_date)}</td>
      <td className={`px-5 py-3.5 text-sm font-semibold ${r.days_remaining != null && r.days_remaining < 0 ? 'text-[#c83248]' : 'text-[#323338]'}`}>{daysText(r)}</td>
      <td className="px-5 py-3.5">
        <div className="flex flex-wrap items-center gap-2">
          <span data-testid={`register-status-${r.property_id}-${r.item}`}
            className={`inline-flex min-w-[110px] items-center justify-center rounded px-3 py-1 text-xs font-bold ${st.cls}`}>
            {st.label}
          </span>
          {r.job_ref && <span className="text-xs font-semibold text-[#2475e6]">{r.job_ref}</span>}
          {r.item === 'gas_safety' && (r.status === 'warning' || r.status === 'not_required') && (
            <GasToggle r={r} onChanged={onChanged} />
          )}
        </div>
      </td>
    </tr>
  );
}

function HmoGroup({ rows, open, toggle, withProperty }) {
  const worst = rows.reduce((a, r) => (r.rank < a.rank ? r : a), rows[0]);
  const st = STATUS_STYLE[worst.status];
  return (
    <>
      <tr className="cursor-pointer border-b border-[#E6E9EF] bg-[#F8F8FA] transition-colors hover:bg-[#F4F5F7]" onClick={toggle}
        data-testid={`register-hmo-toggle-${worst.property_id}`}>
        <td className="px-5 py-2.5 text-sm font-medium text-[#323338]">{withProperty ? worst.property : ''}</td>
        <td className="px-5 py-2.5 text-sm font-bold text-[#323338]" colSpan={3}>
          <span className="inline-flex items-center gap-1.5">
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />} HMO items · {rows.length}
          </span>
        </td>
        <td className="px-5 py-2.5">
          <span className={`inline-flex min-w-[110px] items-center justify-center rounded px-3 py-1 text-xs font-bold ${st.cls}`}>{st.label}</span>
        </td>
      </tr>
      {open && rows.map((r) => <RegisterRow key={r.item} r={r} nested />)}
    </>
  );
}

function GasToggle({ r, onChanged }) {
  const [busy, setBusy] = useState(false);
  const setFlag = async (v) => {
    setBusy(true);
    await fetch(`${API}/api/compliance/property-flags/${r.property_id}`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ no_gas_supply: v }) });
    setBusy(false); onChanged();
  };
  const noGas = r.status === 'not_required';
  return (
    <button type="button" disabled={busy} onClick={() => setFlag(!noGas)} data-testid={`gas-toggle-${r.property_id}`}
      className="inline-flex items-center gap-1 rounded border border-[#E6E9EF] bg-white px-2 py-1 text-xs font-semibold text-[#676879] transition-colors hover:border-[#0073EA] hover:text-[#0073EA] disabled:opacity-60">
      <Flame size={13} /> {noGas ? 'Property has gas' : 'No gas supply'}
    </button>
  );
}

function Pipeline({ jobs, onChanged }) {
  // Completed-but-not-closed renewals (certificate / serving still owed) get their own column, not Completed.
  const colFor = (j) => {
    const st = closeoutStageFor(j);
    return st && st !== 'closed' ? 'closeout' : _displayColumnFor(j);
  };
  const base = DISPLAY_COLUMNS.filter((c) => c.key !== 'completed');
  const done = DISPLAY_COLUMNS.filter((c) => c.key === 'completed');
  const cols = [...base, { key: 'closeout', label: 'To close out' }, ...done]
    .map((c) => ({ ...c, items: jobs.filter((j) => colFor(j) === c.key) }));
  return (
    <div className="flex snap-x gap-4 overflow-x-auto pb-4 xl:grid xl:grid-cols-7 xl:overflow-visible" data-testid="compliance-pipeline">
      {cols.map((c) => (
        <div key={c.key} className="flex min-w-[260px] max-w-[260px] shrink-0 snap-start flex-col gap-3 rounded-xl border border-[#E6E9EF] bg-[#F4F5F7] p-3 xl:min-w-0 xl:max-w-none"
          data-testid={`pipeline-col-${c.key}`}>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#676879]">{c.label}</span>
            <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold text-[#323338]">{c.items.length}</span>
          </div>
          {c.items.map((j) => <PipelineCard key={j.id} j={j} onChanged={onChanged} />)}
          {!c.items.length && <span className="py-6 text-center text-xs text-[#C3C6D4]">No jobs</span>}
        </div>
      ))}
    </div>
  );
}

function PipelineCard({ j, onChanged }) {
  const label = (j.title || '').replace(/ renewal$/, '');
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-[#E6E9EF] bg-white p-4 shadow-sm transition-colors hover:border-[#0073EA]"
      data-testid={`pipeline-card-${j.id}`}>
      <span className="text-sm font-bold text-[#323338]">{label || 'Certificate'} renewal</span>
      <span className="text-xs font-medium text-[#676879]">{j.property_address || j.property_address_line1 || ''}</span>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold text-[#2475e6]">{j.contractor_ref}</span>
        {j.certificate_expiry_date && j.status !== 'completed' && <span className="text-[#676879]">expires {fmtDate(j.certificate_expiry_date)}</span>}
        {j.renewal_doc_id && <span className="font-semibold text-[#009e5a]">New certificate recorded</span>}
      </div>
      {j.assigned_contractor_name && <span className="text-xs text-[#323338]">{j.assigned_contractor_name}</span>}
      <ComplianceCloseoutPrompt r={j} onChanged={onChanged} />
    </div>
  );
}
